import './VideoGuidesPage.css';
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import { videoGuideSections } from './videoGuidesData';

// Placeholder embed shown until Austin provides the real video links/embed code.
const PLACEHOLDER_VIDEO_URL = 'https://www.youtube.com/embed/aqz-KE-bpK';

const getSectionVideos = (section) =>
  section.subSections ? section.subSections.flatMap((sub) => sub.videos) : section.videos;

const allVideos = videoGuideSections.flatMap(getSectionVideos);

const findVideo = (videoId) => allVideos.find((video) => video.id === videoId);

// Maps a video id to the ids of the section (and sub-section) containing it,
// so the sidebar can expand the right groups when a video is deep-linked.
const findVideoLocation = (videoId) => {
  for (const section of videoGuideSections) {
    if (section.videos?.some((video) => video.id === videoId)) {
      return [section.id];
    }
    for (const sub of section.subSections || []) {
      if (sub.videos.some((video) => video.id === videoId)) {
        return [section.id, sub.id];
      }
    }
  }
  return [];
};

const VideoGuidesPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedVideoId = searchParams.get('video') || allVideos[0].id;
  const selectedVideo = findVideo(selectedVideoId) || allVideos[0];

  const [searchQuery, setSearchQuery] = useState('');
  const [expandedIds, setExpandedIds] = useState(
    () => new Set(findVideoLocation(selectedVideo.id))
  );

  const toggleExpanded = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const selectVideo = (videoId) => {
    setSearchParams({ video: videoId });
    setExpandedIds((prev) => new Set([...prev, ...findVideoLocation(videoId)]));
  };

  const query = searchQuery.trim().toLowerCase();
  const matchesQuery = (video) => video.title.toLowerCase().includes(query);

  // While searching, only sections containing a match are shown and they are
  // forced open so the matches are visible.
  const visibleSections = useMemo(() => {
    if (!query) return videoGuideSections;
    return videoGuideSections
      .map((section) => {
        if (section.subSections) {
          const subSections = section.subSections
            .map((sub) => ({ ...sub, videos: sub.videos.filter(matchesQuery) }))
            .filter((sub) => sub.videos.length > 0);
          return { ...section, subSections };
        }
        return { ...section, videos: section.videos.filter(matchesQuery) };
      })
      .filter((section) =>
        section.subSections ? section.subSections.length > 0 : section.videos.length > 0
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const isExpanded = (id) => Boolean(query) || expandedIds.has(id);

  const renderVideoItem = (video, nested) => (
    <li
      key={video.id}
      className={`video-guides-item ${nested ? 'video-guides-item-nested' : ''} ${
        video.id === selectedVideo.id ? 'video-guides-item-selected' : ''
      }`}
      onClick={() => selectVideo(video.id)}
    >
      {video.title}
    </li>
  );

  const renderGroupHeader = (group, nested) => (
    <div
      className={`video-guides-group-header ${nested ? 'video-guides-group-header-nested' : ''}`}
      onClick={() => toggleExpanded(group.id)}
    >
      {isExpanded(group.id) ? (
        <ArrowDropDownIcon fontSize="small" />
      ) : (
        <ArrowRightIcon fontSize="small" />
      )}
      <span>{group.title}</span>
    </div>
  );

  return (
    <div className="video-guides-page">
      <div className="video-guides-header">
        <h1>TSP Video Guides</h1>
        <strong style={{ fontSize: 14 }}>
          Learn how to use TSP through quick, step-by-step video guides.
        </strong>
        <p className="video-guides-support-note">
          Need additional help? Visit our{' '}
          <a
            href="https://www.simple-platform.com/support"
            target="_blank"
            rel="noopener noreferrer"
          >
            Support page
          </a>{' '}
          for more resources or to get in touch with our team.
        </p>
      </div>

      <div className="video-guides-body">
        <div className="video-guides-sidebar">
          <div className="video-guides-search">
            <SearchIcon fontSize="small" />
            <input
              type="text"
              placeholder="Search Video Guides"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
            />
          </div>

          <div className="video-guides-nav">
            {visibleSections.map((section) => (
              <div key={section.id}>
                {renderGroupHeader(section, false)}
                {isExpanded(section.id) &&
                  (section.subSections ? (
                    section.subSections.map((sub) => (
                      <div key={sub.id}>
                        {renderGroupHeader(sub, true)}
                        {isExpanded(sub.id) && (
                          <ul className="video-guides-list">
                            {sub.videos.map((video) => renderVideoItem(video, true))}
                          </ul>
                        )}
                      </div>
                    ))
                  ) : (
                    <ul className="video-guides-list">
                      {section.videos.map((video) => renderVideoItem(video, false))}
                    </ul>
                  ))}
              </div>
            ))}
            {visibleSections.length === 0 && (
              <p className="video-guides-no-results">No video guides found.</p>
            )}
          </div>
        </div>

        <div className="video-guides-content">
          <h2>{selectedVideo.title}</h2>
          <iframe
            className="video-guides-player"
            src={selectedVideo.videoUrl || PLACEHOLDER_VIDEO_URL}
            title={selectedVideo.title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
          <p className="video-guides-description">{selectedVideo.description}</p>
        </div>
      </div>
    </div>
  );
};

export default VideoGuidesPage;
